import { computed, ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { Routes } from '@/router/router.const';
import elementsIds from '@/const/elementIds.json';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { useOsService } from '@/services/OsService/OsService';
import { SettingsLinuxTabsKeys, SettingsWindowsTabsKeys } from './SettingsTabs.const';
import { SettingsTab } from './SettingsTabs.types';

export const useSettingsTabs = () => {
  const route = useRoute();
  const userRolePermissions = useUserRolePermissions();
  const osService = useOsService();

  const settingsTabWindows = [
    {
      name: SettingsWindowsTabsKeys.General,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.general,
      label: 'General',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.general,
      },
    },
    {
      name: SettingsWindowsTabsKeys.Agents,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.agents,
      label: 'Agents',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.agents,
      },
    },
    {
      name: SettingsWindowsTabsKeys.ApiAccess,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.apiAccess,
      label: 'API Access',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.apiAccess,
      },
    },
    {
      name: SettingsWindowsTabsKeys.Cloud,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.cloud,
      label: 'Cloud',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.cloud,
      },
    },
  ];

  const settingsTabsLinux = [
    {
      name: SettingsLinuxTabsKeys.General,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.general,
      label: 'General',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.general,
      },
    },
    {
      name: SettingsWindowsTabsKeys.ApiAccess,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.apiAccess,
      label: 'API Access',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.apiAccess,
      },
    },
    {
      name: SettingsLinuxTabsKeys.Cloud,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.cloud,
      label: 'Cloud',
      active: false,
      attributes: {
        'data-testid': elementsIds.settings.tabs.cloud,
      },
    },
  ];

  const settingsTabsMac = [
    {
      name: SettingsLinuxTabsKeys.General,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.general,
      label: 'General',
      active: route.name === Routes.SettingsGeneral,
      attributes: {
        'data-testid': elementsIds.settings.tabs.general,
      },
    },
    {
      name: SettingsWindowsTabsKeys.ApiAccess,
      authorized: !!userRolePermissions.authorize?.value?.settings.visible.apiAccess,
      label: 'API Access',
      active: route.name === Routes.SettingsApiAccess,
      attributes: {
        'data-testid': elementsIds.settings.tabs.apiAccess,
      },
    },
  ];

  const settingsTabs: Record<OsType, ComputedRef<SettingsTab[]>> = {
    [OsType.Windows]: computed<SettingsTab[]>(() => settingsTabWindows),
    [OsType.Linux]: computed<SettingsTab[]>(() => settingsTabsLinux),
    [OsType.Mac]: computed<SettingsTab[]>(() => settingsTabsMac),
  };
  return computed<SettingsTab[]>(() => {
    const tabs = osService?.osType.value ? settingsTabs[osService?.osType.value].value : [];
    return tabs
      .filter(tab => tab.authorized)
      .map(tab => ({
        ...tab,
        active: route.name === `settings.${tab.name}`,
      }));
  });
};
